import React, { useState } from "react";
import { Button, Empty, Spin, Input, Switch, Space, message, Card, Badge, Divider } from "antd";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import useNotificationData from "../hooks/useNotificationData";

const channels = ["PUSH", "SMS", "EMAIL", "WHATSAPP"];

const NotificationPreferencesManager = () => {
  const { preferences, loading, fetchPreferences, updatePreference, updateAllPreferences } =
    useNotificationData();
  const [searchUserId, setSearchUserId] = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [localPrefs, setLocalPrefs] = useState({});
  const [searched, setSearched] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalPrefs, setOriginalPrefs] = useState({});

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const handleSearch = async () => {
    if (!searchUserId.trim()) {
      message.warning("Please enter a user ID or email");
      return;
    }
    const result = await fetchPreferences(searchUserId);
    if (result.success) {
      setCurrentUserId(searchUserId);
      setSearched(true);
      const prefs = {};
      preferences.forEach((p) => {
        prefs[p.channel] = p.isOptedIn;
      });
      setLocalPrefs(prefs);
      setOriginalPrefs(JSON.parse(JSON.stringify(prefs)));
      setHasChanges(false);
    } else {
      message.error("User not found");
      setSearched(false);
    }
  };

  const handleToggleChannel = (channel, currentState) => {
    const newState = !currentState;
    setLocalPrefs({ ...localPrefs, [channel]: newState });
    setHasChanges(true);
  };

  const handleBulkUpdate = async () => {
    const bulkData = {};
    channels.forEach((ch) => {
      bulkData[ch] = localPrefs[ch] ?? false;
    });
    const result = await updateAllPreferences(currentUserId, bulkData);
    if (result.success) {
      message.success("All preferences updated");
      const newPrefs = {};
      preferences.forEach((p) => {
        newPrefs[p.channel] = p.isOptedIn;
      });
      setOriginalPrefs(JSON.parse(JSON.stringify(newPrefs)));
      setHasChanges(false);
    } else {
      message.error("Failed to update preferences");
    }
  };

  const handleReset = () => {
    setLocalPrefs(JSON.parse(JSON.stringify(originalPrefs)));
    setHasChanges(false);
  };

  const getChannelColor = (isOptedIn) => {
    return isOptedIn ? "#52c41a" : "#d9d9d9";
  };

  return (
    <Spin spinning={loading} indicator={antIcon}>
      <div className="pb-10">
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-4">User Notification Preferences</h3>

          <div className="bg-white p-4 rounded-lg mb-6">
            <div className="flex gap-2">
              <Input
                prefix={<SearchOutlined />}
                placeholder="Enter user ID or email"
                value={searchUserId}
                onChange={(e) => setSearchUserId(e.target.value)}
                onPressEnter={handleSearch}
              />
              <Button type="primary" onClick={handleSearch} loading={loading}>
                Search
              </Button>
            </div>
          </div>

          {searched && currentUserId && (
            <div>
              {preferences.length === 0 ? (
                <Empty description="No preferences found for this user" />
              ) : (
                <div className="bg-white p-5 rounded-lg">
                  <div className="mb-6 pb-4 border-b">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>User:</strong>
                    </p>
                    <p className="text-sm font-mono font-medium text-gray-900">{currentUserId}</p>
                  </div>

                  <h4 className="font-bold text-base mb-4">Channel Preferences</h4>

                  <div className="space-y-4 mb-6">
                    {channels.map((channel) => {
                      const pref = preferences.find((p) => p.channel === channel);
                      const isOptedIn = localPrefs[channel] ?? pref?.isOptedIn ?? false;

                      return (
                        <div key={channel} className="border-b pb-4 last:border-b-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge
                                color={getChannelColor(isOptedIn)}
                                text={
                                  <span className="font-medium text-sm">{channel}</span>
                                }
                              />
                              <span className={`text-xs px-2 py-1 rounded ${isOptedIn ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                                {isOptedIn ? "Opted In" : "Opted Out"}
                              </span>
                            </div>
                            <Switch
                              checked={isOptedIn}
                              onChange={() => handleToggleChannel(channel, isOptedIn)}
                              loading={loading}
                            />
                          </div>

                          {pref && (
                            <p className="text-xs text-gray-500 ml-6">
                              Last updated: {new Date(pref.updatedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <Divider style={{ margin: "16px 0" }} />

                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">
                      {hasChanges
                        ? "You have unsaved changes"
                        : "All preferences saved"}
                    </p>
                    <Space className="w-full" size="small">
                      <Button onClick={() => setSearched(false)}>
                        Clear
                      </Button>
                      {hasChanges && (
                        <>
                          <Button onClick={handleReset}>
                            Reset
                          </Button>
                          <Button type="primary" onClick={handleBulkUpdate}>
                            Save Changes
                          </Button>
                        </>
                      )}
                    </Space>
                  </div>
                </div>
              )}
            </div>
          )}

          {!searched && (
            <Card className="text-center bg-blue-50 border-blue-200">
              <p className="text-gray-600 text-sm mb-2">
                🔍 Search for a user to manage their notification preferences
              </p>
              <p className="text-gray-500 text-xs">
                Enter user ID or email address and click Search
              </p>
            </Card>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default NotificationPreferencesManager;
